import { PT_HUES } from './config';
import type { PtDirectoryPatient, PtFlag, PtHealthStatus } from './types';

export function ptFormatMoney(n: number): string {
  if (n === 0 || n == null) return '—';
  if (n >= 1000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `$${n.toLocaleString()}`;
}

export function ptFormatMoneyFull(n: number): string {
  if (n === 0 || n == null) return '$0';
  return `$${n.toLocaleString()}`;
}

export function ptRollupHealth(p: PtDirectoryPatient): PtHealthStatus {
  if (p.premedicate && !p.alert) return 'critical';
  if (!p.phone && !p.email) return 'critical';
  if (p.balance > 500 && !p.insurance) return 'critical';
  if (p.alert && !p.confirmed) return 'critical';
  if (p.newPatient && !p.confirmed) return 'critical';
  if (p.triageItems > 1) return 'critical';

  const missing = [p.confirmed, p.insurance, p.forms].filter((x) => !x).length;
  if (missing >= 1) return 'attention';
  if (p.balance > 0) return 'attention';
  if (p.triageItems > 0) return 'attention';
  if (p.newPatient) return 'attention';
  if (p.nonEnglish) return 'attention';
  return 'ready';
}

export function ptPatientDisplayName(p: PtDirectoryPatient): string {
  return p.title ? `${p.title} ${p.first} ${p.last}` : `${p.first} ${p.last}`;
}

export function ptDirectoryRouteId(p: PtDirectoryPatient): string {
  return p.profileId ?? p.id;
}

export function ptHueForInitials(initials: string): string {
  const code = (initials.charCodeAt(0) + (initials.charCodeAt(1) || 0)) % PT_HUES.length;
  return PT_HUES[code]!;
}

export function ptFlags(patient: PtDirectoryPatient): PtFlag[] {
  const flags: PtFlag[] = [];
  if (patient.triageItems > 0) {
    flags.push({
      tone: 'gold',
      icon: 'alert',
      label:
        patient.triageItems === 1 ? 'In triage' : `${patient.triageItems} in triage`,
      title: patient.triageReason || 'Open Triage Inbox items',
    });
  }
  if (patient.alert) {
    flags.push({
      tone: 'warning',
      icon: 'heart',
      label: patient.alert.length > 22 ? 'Medical alert' : patient.alert,
      title: patient.alert,
    });
  }
  if (patient.balance > 0) {
    flags.push({
      tone: 'gold',
      icon: 'dollar',
      label: `${ptFormatMoneyFull(patient.balance)} balance`,
    });
  }
  if (patient.planValue > 0) {
    flags.push({
      tone: 'info',
      icon: 'clipboard',
      label: `${ptFormatMoney(patient.planValue)} plan`,
    });
  }
  if (patient.newPatient) {
    flags.push({ tone: 'ai', icon: 'sparkle', label: 'New patient' });
  }
  if (patient.nonEnglish) {
    flags.push({ tone: 'info', icon: 'globe', label: patient.lang });
  }
  if (!patient.phone && !patient.email) {
    flags.push({ tone: 'warning', icon: 'phone', label: 'No contact' });
  }
  return flags;
}

export function ptSearchPatients(
  patients: PtDirectoryPatient[],
  query: string,
): PtDirectoryPatient[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return patients.filter((p) => {
    const name = `${p.title || ''} ${p.first} ${p.last}`.toLowerCase();
    return (
      name.includes(q) ||
      (p.phone || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.procedure || '').toLowerCase().includes(q) ||
      (p.provider || '').toLowerCase().includes(q)
    );
  });
}

export function patientsDirectoryHeaderStats(
  today: PtDirectoryPatient[],
  tomorrow: PtDirectoryPatient[],
) {
  const all = [...today, ...tomorrow];
  return {
    total: all.length,
    active: all.filter((p) => ptRollupHealth(p) === 'ready').length,
    overdue: all.filter((p) => ptRollupHealth(p) === 'critical').length,
  };
}
